import type { CommandAction, CommandArgSpec, CommandArgs, CommandIntent, CommandResolution, CommandResolver } from "./commands.types";

const NUMBER_WORDS: Record<string, string> = {
  one: "1", two: "2", three: "3", four: "4", five: "5", six: "6", seven: "7", eight: "8", nine: "9", ten: "10",
};
const FILLER = new Set(["a", "an", "the", "please"]);

/** Lower case, no quotes or `.,!?`, no articles or "please", no leading "can you"/"could you", number words as digits. */
export const normalizeCommandText = (text: string): string => {
  const cleaned = text.toLowerCase().replace(/["'`“”‘’.,!?]/g, " ").replace(/\s+/g, " ").trim();
  const withoutLead = cleaned.replace(/^(can|could) you\s+/, "");
  return withoutLead.split(" ").filter((word) => word && !FILLER.has(word)).map((word) => NUMBER_WORDS[word] ?? word).join(" ");
};

interface CompiledPattern { regex: RegExp; names: string[] }
const PLACEHOLDER = /\{(\w+)\}/g;
const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const compilePattern = (template: string, specs: CommandArgSpec[]): CompiledPattern | null => {
  const normalized = normalizeCommandText(template);
  const names: string[] = [];
  let source = "";
  let last = 0;
  for (const match of Array.from(normalized.matchAll(PLACEHOLDER))) {
    const spec = specs.find((candidate) => candidate.name === match[1]);
    if (!spec) return null;
    source += escapeRegex(normalized.slice(last, match.index));
    source += spec.kind === "number" ? "(\\d+)" : "(.+?)";
    names.push(match[1]);
    last = (match.index ?? 0) + match[0].length;
  }
  source += escapeRegex(normalized.slice(last));
  return { regex: new RegExp(`^${source}$`), names };
};

type EntitySpec = Extract<CommandArgSpec, { kind: "entity" }>;
type EntityHit = { kind: "one"; id: string } | { kind: "many"; labels: string[] } | { kind: "none" };

/** Fewer characters than this never prefix- or substring-match: "it" is not a chart. */
const MIN_PARTIAL_LENGTH = 3;
/** Pronouns and bare nouns for the thing itself ("duplicate chart" is not a chart called "chart"). */
const PRONOUNS = new Set([
  "it", "its", "this", "that", "these", "those", "them", "they", "here", "there", "one", "ones",
  "chart", "charts", "graph", "tile", "widget", "type", "tab",
]);
const isPronounLike = (query: string): boolean => query.split(" ").every((word) => PRONOUNS.has(word));

/**
 * Exact label, then unique prefix, then unique substring. A tier with several hits is ambiguous. Prefix and
 * substring need at least MIN_PARTIAL_LENGTH characters and must not be only pronouns ("edit it", "edit that").
 */
const resolveEntity = (query: string, spec: EntitySpec): EntityHit => {
  const options = spec.options().map((option) => ({ ...option, key: normalizeCommandText(option.label) }));
  const partial = query.length >= MIN_PARTIAL_LENGTH && !isPronounLike(query);
  const tiers: ((key: string) => boolean)[] = [
    (key) => key === query,
    ...(partial ? [(key: string) => key.startsWith(query), (key: string) => key.includes(query)] : []),
  ];
  for (const test of tiers) {
    const hits = options.filter((option) => test(option.key));
    if (hits.length === 1) return { kind: "one", id: hits[0].id };
    if (hits.length > 1) return { kind: "many", labels: hits.map((hit) => hit.label) };
  }
  return { kind: "none" };
};

const ambiguousMessage = (spec: EntitySpec, query: string, labels: string[]): string =>
  `More than one ${spec.entity} matches "${query}": ${labels.join(", ")}. Which one?`;

type Attempt = { kind: "intent"; args: CommandArgs; weight: number } | { kind: "ambiguous"; message: string } | null;

const attempt = (compiled: CompiledPattern, text: string, specs: CommandArgSpec[]): Attempt => {
  const match = compiled.regex.exec(text);
  if (!match) return null;
  const args: CommandArgs = {};
  let weight = 0;
  let ambiguous: string | null = null;
  for (let index = 0; index < compiled.names.length; index += 1) {
    const spec = specs.find((candidate) => candidate.name === compiled.names[index]);
    const value = match[index + 1];
    if (!spec) return null;
    if (spec.kind === "number") {
      const count = Number(value);
      if (!Number.isFinite(count) || (spec.min !== undefined && count < spec.min)) return null;
      args[spec.name] = spec.max !== undefined ? Math.min(count, spec.max) : count;
    } else if (spec.kind === "text") {
      args[spec.name] = value;
      weight += 1;
    } else {
      weight += 1;
      const hit = resolveEntity(value, spec);
      if (hit.kind === "none") return null;
      if (hit.kind === "many") ambiguous = ambiguous ?? ambiguousMessage(spec, value, hit.labels);
      else args[spec.name] = hit.id;
    }
  }
  return ambiguous ? { kind: "ambiguous", message: ambiguous } : { kind: "intent", args, weight };
};

/** Deterministic whole-message matcher over each action's `patterns`. No match: `none` (the text goes to the AI chat). */
export const resolveByRules = (text: string, actions: readonly CommandAction[]): CommandResolution => {
  const normalized = normalizeCommandText(text);
  if (!normalized) return { kind: "none" };
  let best: { intent: CommandIntent; weight: number } | null = null;
  let ambiguous: string | null = null;
  for (const action of actions) {
    const specs = action.args ?? [];
    for (const template of action.patterns ?? []) {
      const compiled = compilePattern(template, specs);
      if (!compiled) continue;
      const result = attempt(compiled, normalized, specs);
      if (!result) continue;
      if (result.kind === "ambiguous") ambiguous = ambiguous ?? result.message;
      else if (!best || result.weight < best.weight) {
        best = { intent: { actionId: action.id, args: result.args, via: "rule" }, weight: result.weight };
      }
    }
  }
  if (best) return { kind: "intent", intent: best.intent };
  if (ambiguous) return { kind: "ambiguous", message: ambiguous };
  return { kind: "none" };
};

export const ruleResolver: CommandResolver = {
  id: "rules",
  resolve: async (text, actions) => resolveByRules(text, actions),
};
